import React, { useEffect, useState } from 'react';
import {
  GridItem,
  Heading,
  HStack,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  Box,
  Icon,
  useColorModeValue,
  Container,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
} from '@chakra-ui/react';
import { FaMoneyBillWave, FaCalendarCheck, FaCalendar } from 'react-icons/fa';
import ProductsToPayList from '../components/products-to-pay-list';
import ProductFeeCard from '../components/product-fee-card';
import {getActivityFeesForMembershipFeeByMemberId, getMembershipFeesByMember} from '../services/product-service';
import { useAuth } from '../components/auth';
import { Member } from '../models/member';
import { MembershipFeesResponse } from '../models/web/membership-fee-response';
import { MemberProductStatus } from "../models/periodic-product-payment";
import PaymentMethods from "../components/payment-methods";
import LoadingSpinner from '../components/loading-spinner';
import {PeriodicProduct} from "../models/periodic-product";
import ActivityFeesForMembershipFeeSection from "../components/activity-fees-for-membership-fee";
import {getMember} from "../services/member-service";
import { Activity } from '../models/activity';
function MyMembershipFees(): JSX.Element {
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [member] = useState<Member | null>(auth?.user?.selectedMember);
  const [membershipFeesResponse, setMembershipFeesResponse] = useState<MembershipFeesResponse | null>(null);
  const [membershipFeesPaymentData, setMembershipFeesPaymentData] = useState<
    Map<string, MemberProductStatus[]>
  >(new Map());
  const [membershipFeesPaymentDataYears, setMembershipFeesPaymentDataYears] =
    useState<string[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [membershipFeesToPayList, setMembershipFeesToPayList] = useState<MemberProductStatus[]>([]);
  const [activitiesFees, setActivitiesFees] = useState<PeriodicProduct[]>([]);
  const [memberActivities, setMemberActivities] = useState<Activity[]>([]);
  const [showActivities, setShowActivities] = useState(true);
  const [loadingProductId, setLoadingProductId] = useState<string | null>(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('green.100', 'green.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    if (member === null || !isLoading) return;

    const getMembershipFees = async (): Promise<void> => {
      const response = (await getMembershipFeesByMember(member.id)).response;
      if (response !== null) {
        setMembershipFeesResponse(response);
      }
      setIsLoading(false);
    }

    const getMemberActivities = async (): Promise<void> => {
      const response = (await getMember(member.id!)).response;
      if (response !== null && response.memberActivities) {
        setMemberActivities(response.memberActivities.map(x => x.activity));
      }
    }

    if (!membershipFeesResponse) {
      getMembershipFees().catch(() => {});
    }

    getMemberActivities().catch(() => {});
  }, [member, isLoading, membershipFeesResponse]);

  useEffect(() => {
    if (membershipFeesResponse === null) return;

    const keys = Object.keys(membershipFeesResponse.membershipFeesPayments);
    const entries = Object.entries(membershipFeesResponse.membershipFeesPayments);
    const map = new Map(entries);
    setMembershipFeesPaymentData(map);
    setMembershipFeesPaymentDataYears(keys);
  }, [membershipFeesResponse]);

  useEffect(() => {
    if (membershipFeesResponse == null) { return; }

    const index = membershipFeesResponse.firstNonPaidMembershipFeeYear != null
      ? membershipFeesPaymentDataYears.indexOf(membershipFeesResponse.firstNonPaidMembershipFeeYear.toString())
      : membershipFeesPaymentDataYears.length - 1;
    setTabIndex(index);
  }, [membershipFeesPaymentDataYears, membershipFeesResponse])


  const addMembershipFeeToList = async (membershipFee: MemberProductStatus): Promise<void> => {
    let activitiesFeesForMembershipFee: MemberProductStatus[] | null = [];
    if (showActivities) {
      setLoadingProductId(membershipFee.product.id ?? null);
      try {
        activitiesFeesForMembershipFee = (await getActivityFeesForMembershipFeeByMemberId(member?.id!, membershipFee.product.id!)).response;
        activitiesFeesForMembershipFee = activitiesFeesForMembershipFee?.filter(x => !x.isCompleted) ?? [];
        const newActivitiesFees = activitiesFees.concat(activitiesFeesForMembershipFee?.map(x => x.product) ?? []);
        setActivitiesFees(newActivitiesFees);
      } finally {
        setLoadingProductId(null);
      }
    }

    const index = membershipFeesToPayList?.findIndex(
      (existingElement: { productId: string }) =>
        existingElement?.productId === membershipFee?.product.id
    );

    if (index > -1) {
      // Removing
      const membershipFeesListWithoutElement = membershipFeesToPayList.filter((v, i) => i < index);
      setMembershipFeesToPayList(membershipFeesListWithoutElement);
      setActivitiesFees([]);
    } else {
      // Adding
      const activitiesFeesToPay = [];
      for (const activityFee of activitiesFeesForMembershipFee ?? []) {
        activitiesFeesToPay.push(activityFee);
      }

      membershipFeesToPayList.push(membershipFee);
      const newToPayList = membershipFeesToPayList.concat(activitiesFeesToPay);
      setMembershipFeesToPayList(newToPayList);
    }
  };

  const getAllMembershipFees = (): MemberProductStatus[] => {
    const result: MemberProductStatus[] = [];
    for (const year of membershipFeesPaymentDataYears) {
      result.push(...(membershipFeesPaymentData.get(year) ?? []));
    }
    return result;
  };

  const getMembershipFees = (
    year: string
  ): JSX.Element | JSX.Element[] | undefined => {
    const allMembershipFees = getAllMembershipFees();
    return membershipFeesPaymentData.has(year) &&
      membershipFeesPaymentData.get(year) != null ? (
      membershipFeesPaymentData
        .get(year)
        ?.map((membershipFee: MemberProductStatus, index: React.Key) => (
          <ProductFeeCard
            key={index}
            productFee={membershipFee}
            cardTitle="CUOTA SOCIAL"
            variant="membership"
            toPayList={membershipFeesToPayList}
            allProducts={allMembershipFees}
            onChange={addMembershipFeeToList}
            isLoading={loadingProductId === membershipFee.product.id}
          />
        ))
    ) : (
      <></>
    );
  };

  const getTabForEachYear = (): JSX.Element | JSX.Element[] => {
    if (membershipFeesPaymentDataYears.length === 0) {
      return <></>;
    }

    return membershipFeesPaymentDataYears.map((year: string) => (
      <Tab key={year}>
        <Icon as={FaCalendarCheck} mr={2} />
        {year}
      </Tab>
    ));
  };

  const getTabPanelForEachYear = (): JSX.Element | JSX.Element[] => {
    if (membershipFeesPaymentDataYears.length === 0) {
      return <></>;
    }

    return membershipFeesPaymentDataYears.map((year: string) => (
      <TabPanel key={year} px={0} py={6}>
        <SimpleGrid columns={[1, 2, 3, 4]} spacing={6}>
          {getMembershipFees(year)}
        </SimpleGrid>
      </TabPanel>
    ));
  };

  const getMembershipFeesToPay = (): JSX.Element => {
    if (membershipFeesToPayList.length === 0) {
      return <></>;
    }

    let title = "Cuotas a abonar";
    let text = "Revisá atentamente. Vas a abonar las siguientes cuotas sociales:";
    if (activitiesFees && showActivities) {
      title = "Cuotas y actividades a abonar";
      text = "Revisá atentamente. Vas a abonar las siguientes cuotas sociales y aranceles de actividades:";
    }

    return (
      <Box
        bg={cardBg}
        p={6}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        boxShadow="lg"
        width="100%"
      >
        <VStack spacing={4} align="stretch">
          <HStack>
            <Icon as={FaMoneyBillWave} color="green.500" />
            <Heading as="h3" size="md">{title}</Heading>
          </HStack>
          <Text color={secondaryTextColor}>{text}</Text>
          <ProductsToPayList
            membershipFeesToPay={membershipFeesToPayList}
            tableSize="sm"
            variant="default"
          />
        </VStack>
      </Box>
    );
  };

  const handleChange = (index: number): void => {
    setTabIndex(index);
  };

  const removeActivitiesFeesFromList = () => {
    setMembershipFeesToPayList(membershipFeesToPayList.filter(x => x.product.discriminator === "MembershipFee"));
    setShowActivities(false);
  }

  const getStatusAlert = () => {
    if (!membershipFeesResponse) return null;

    if (membershipFeesResponse.pendingMembershipFees === 0) {
      return (
        <Alert
          status="success"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          borderRadius="lg"
          mb={6}
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            ¡Estás al día!
          </AlertTitle>
          <AlertDescription maxWidth="sm">
            No tenés cuotas sociales pendientes de pago. ¡Gracias por mantenerte al día!
          </AlertDescription>
        </Alert>
      );
    } else if (membershipFeesResponse.pendingMembershipFees > 0) {
      return (
        <Alert
          status="warning"
          variant="left-accent"
          borderRadius="lg"
          mb={6}
        >
          <AlertIcon />
          <Box>
            <AlertTitle>Cuotas pendientes</AlertTitle>
            <AlertDescription>
              Tenés cuotas sociales pendientes de pago. Seleccioná las que quieras abonar haciendo clic en "Seleccionar para abonar".
            </AlertDescription>
          </Box>
        </Alert>
      );
    }
    return null;
  };

  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={6} align="stretch">
        <Box
          bg={headerBg}
          p={6}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="sm"
        >
                     <HStack>
             <Icon as={FaCalendar} color="green.500" boxSize={6} />
             <Heading color={textColor}>
               Mis cuotas sociales
             </Heading>
           </HStack>
        </Box>

        {/* Status Alert */}
        {!isLoading && getStatusAlert()}

        {/* Content */}
        {isLoading ? (
          <LoadingSpinner label="Cargando tus cuotas sociales..." />
        ) : (
          <VStack spacing={6} align="stretch">
                    <Box
          bg={cardBg}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="sm"
          overflow="hidden"
          p={4}
        >
              <Tabs
                variant="soft-rounded"
                colorScheme="green"
                isFitted
                index={tabIndex}
                onChange={handleChange}
              >
                <TabList flexWrap="wrap" gap={2} mb={2}>
                  {getTabForEachYear()}
                </TabList>
                <TabPanels>
                  {getTabPanelForEachYear()}
                </TabPanels>
              </Tabs>
            </Box>


                    <SimpleGrid columns={[1, 1, 2]} spacing={8}>
              <GridItem>
                <VStack spacing={6} align="stretch">
                  {getMembershipFeesToPay()}
                  {activitiesFees.length > 0 && showActivities && (
                    <ActivityFeesForMembershipFeeSection
                      activities={memberActivities}
                      buttonActionOnClick={removeActivitiesFeesFromList}
                    />
                  )}
                </VStack>
              </GridItem>
              <GridItem>
                {membershipFeesToPayList.length > 0 && (
                  <Box
                    bg={cardBg}
                    p={6}
                    borderRadius="lg"
                    border="1px solid"
                    borderColor={borderColor}
                    boxShadow="lg"
                    position={{ base: "sticky", md: "static" }}
                    top={{ base: 6, md: undefined }}
                  >
                    <PaymentMethods productsToPayList={membershipFeesToPayList}/>
                  </Box>
                )}
              </GridItem>
            </SimpleGrid>
          </VStack>
        )}
      </VStack>
    </Container>
  );
}

export default MyMembershipFees;
